import { useState, useRef, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Loader2, Image, ChevronDown, BookOpen, Lock, AlertCircle, Users, Users2 } from "lucide-react";
import axios from "axios";
import { STORY } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const THEMES = [
  { value: "enchanted_forest", label: "Enchanted Forest" },
  { value: "space_adventure", label: "Space Adventure" },
  { value: "ocean_kingdom", label: "Ocean Kingdom" },
  { value: "dragon_castle", label: "Dragon Castle" },
  { value: "fairy_garden", label: "Fairy Garden" },
  { value: "time_travel", label: "Time Travel" },
  { value: "candy_cloud", label: "Candy Cloud World" },
  { value: "moon_village", label: "Moon Village" },
  { value: "dinosaur_valley", label: "Dinosaur Valley" },
  { value: "superhero_city", label: "Superhero City" },
  { value: "pirate_cove", label: "Pirate Cove" },
];

const ILLUSTRATION_STYLES = [
  { value: "watercolor", label: "Soft Watercolor" },
  { value: "cartoon", label: "Bold Cartoon" },
  { value: "pastel", label: "Dreamy Pastel" },
];

export function parseStory(text) {
  if (!text) return [];
  const cleaned = text
    .replace(/^TITLE:.*$/gm, "")
    .replace(/^THEME:.*$/gm, "")
    .replace(/^AGE RANGE:.*$/gm, "")
    .replace(/^ESTIMATED READ TIME:.*$/gm, "")
    .trim();

  const parts = [];
  const regex = /\[ILLUSTRATION:\s*(.*?)\]/gs;
  let lastIndex = 0;
  let match;
  let illIdx = 0;

  while ((match = regex.exec(cleaned)) !== null) {
    const textBefore = cleaned.slice(lastIndex, match.index).trim();
    if (textBefore) parts.push({ type: "text", content: textBefore });
    parts.push({ type: "illustration", prompt: match[1].trim(), index: illIdx++ });
    lastIndex = match.index + match[0].length;
  }
  const remaining = cleaned.slice(lastIndex).trim();
  if (remaining) parts.push({ type: "text", content: remaining });
  return parts;
}

function StoryTextBlock({ content }) {
  const paragraphs = content.split(/\n\n+/).filter(Boolean);
  return (
    <div className="story-body">
      {paragraphs.map((p, i) => (
        <p key={i}>{p.trim()}</p>
      ))}
    </div>
  );
}

function IllustrationCard({ storyId, prompt, index, illustrations, onGenerate, generating }) {
  const existing = illustrations[index];
  const isLoading = generating[index];

  return (
    <motion.div
      data-testid={`${STORY.illustrationCard}-${index}`}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
      className="my-8 rounded-2xl overflow-hidden border border-purple-500/20"
    >
      {existing ? (
        <div className="relative group">
          <img
            src={`${API}/stories/${storyId}/illustrations/${index}`}
            alt={`Story illustration ${index + 1}`}
            className="w-full aspect-[4/3] max-h-[45vh] sm:max-h-[420px] object-cover"
            onError={(e) => { e.target.style.display = "none"; }}
          />
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
            <p className="text-xs text-slate-300 font-serif italic">{prompt}</p>
          </div>
        </div>
      ) : (
        <div className="relative" style={{ background: "rgba(22,18,48,0.5)" }}>
          {isLoading ? (
            <div className="aspect-[4/3] max-h-[45vh] sm:max-h-[420px] flex flex-col items-center justify-center gap-3">
              <div className="relative">
                <div className="w-16 h-16 rounded-full border-2 border-purple-500/30 border-t-amber-400 animate-spin" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Image size={18} className="text-amber-400" />
                </div>
              </div>
              <p className="text-sm text-slate-400 font-serif italic">Painting watercolors...</p>
            </div>
          ) : (
            <div className="aspect-[4/3] max-h-[45vh] sm:max-h-[420px] flex flex-col items-center justify-center gap-4 p-6">
              <div className="w-14 h-14 rounded-full bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
                <Image size={22} className="text-purple-400" />
              </div>
              <div className="text-center max-w-xs">
                <p className="text-xs text-slate-500 font-mono tracking-wider uppercase mb-1">Illustration {index + 1}</p>
                <p className="text-sm text-slate-400 font-serif italic mb-4">{prompt}</p>
                <button
                  data-testid={`${STORY.generateIllBtn}-${index}`}
                  onClick={() => onGenerate(index)}
                  className="px-5 py-2 rounded-full bg-purple-600/30 border border-purple-500/30 text-purple-300 text-xs font-medium hover:bg-purple-600/50 transition-colors"
                >
                  Generate Illustration
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}

export default function StoryGenerator({ user, onAuthRequired, onStoryGenerated }) {
  const [formData, setFormData] = useState({
    child_name: "", child_age: 5, theme: "enchanted_forest", companion_detail: "", moral: "",
    sibling_name: "", sibling_age: 5, illustration_style: "watercolor",
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [storyText, setStoryText] = useState("");
  const [storyId, setStoryId] = useState(null);
  const [storyTitle, setStoryTitle] = useState("");
  const [storyParts, setStoryParts] = useState([]);
  const [illustrations, setIllustrations] = useState({});
  const [generatingIlls, setGeneratingIlls] = useState({});
  const [batchStatus, setBatchStatus] = useState(null);
  const batchPollRef = useRef(null);
  const genPollRef = useRef(null);
  const [parentNote, setParentNote] = useState(null);
  const [error, setError] = useState("");
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const [siblingMode, setSiblingMode] = useState(false);
  const storyScrollRef = useRef(null);

  // Load child profiles for quick-fill
  useEffect(() => {
    if (!user) { setProfiles([]); setSelectedProfileId(null); return; }
    axios.get(`${API}/profiles`, { withCredentials: true })
      .then((res) => setProfiles(res.data))
      .catch(() => {});
  }, [user]);

  // Prefill from URL query params (e.g., Sibling Quick-Pick from /my-stories)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hero = params.get("hero");
    const sibling = params.get("sibling");
    if (!hero && !sibling) return;
    setFormData((f) => ({
      ...f,
      child_name: hero || f.child_name,
      child_age: parseInt(params.get("heroAge") || "", 10) || f.child_age,
      sibling_name: sibling || f.sibling_name,
      sibling_age: parseInt(params.get("siblingAge") || "", 10) || f.sibling_age,
      theme: params.get("theme") || f.theme,
    }));
    if (sibling) setSiblingMode(true);
    // Clear URL after prefill so re-navigation doesn't repeat it
    window.history.replaceState({}, "", `${window.location.pathname}#generator`);
    // Scroll reliably: prefer Lenis when it exists (route through the smooth-scroll owner);
    // otherwise use native scrollTo. Because Lenis loads via dynamic import, retry a few
    // times until it appears so we don't lose the target to Lenis's init (0,0) call.
    const scrollNow = () => {
      const el = document.getElementById("generator");
      if (!el || !el.offsetTop) return false;
      const target = el.offsetTop - 60;
      if (window.__lenis && typeof window.__lenis.scrollTo === "function") {
        window.__lenis.scrollTo(target, { duration: 1.1 });
      } else {
        window.scrollTo({ top: target, behavior: "smooth" });
      }
      return true;
    };
    // Try several times over ~2s so we win regardless of when Lenis mounts.
    const times = [80, 200, 400, 700, 1100, 1600, 2100];
    times.forEach((t) => setTimeout(scrollNow, t));
  }, []);

  const handleSelectProfile = (p) => {
    if (selectedProfileId === p.profile_id) {
      setSelectedProfileId(null);
      return;
    }
    setSelectedProfileId(p.profile_id);
    setFormData((f) => ({
      ...f,
      child_name: p.name,
      child_age: p.age,
      theme: p.favorite_theme || f.theme,
    }));
  };

  // Parse story when generation is done
  useEffect(() => {
    if (!isGenerating && storyText) {
      setStoryParts(parseStory(storyText));
      const wc = storyText.split(/\s+/).filter(Boolean).length;
      setWordCount(wc);
    }
  }, [isGenerating, storyText]);

  // Auto-scroll during generation
  useEffect(() => {
    if (isGenerating && storyScrollRef.current) {
      storyScrollRef.current.scrollTop = storyScrollRef.current.scrollHeight;
    }
  }, [storyText, isGenerating]);

  // Clean up illustration-batch polling on unmount
  useEffect(() => () => {
    if (batchPollRef.current) clearInterval(batchPollRef.current);
    if (genPollRef.current) clearInterval(genPollRef.current);
  }, []);

const ACTIVE_STORY_KEY = "dw_active_story_id";

  const startGenerationPolling = useCallback((sid) => {
    if (genPollRef.current) return;
    genPollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/stories/${sid}/generation-status`, { withCredentials: true });
        const g = res.data;
        setStoryText(g.story_text || "");
        if (g.status === "ready") {
          clearInterval(genPollRef.current);
          genPollRef.current = null;
          localStorage.removeItem(ACTIVE_STORY_KEY);
          setStoryTitle(g.title || "");
          if (g.parent_note) setParentNote(g.parent_note);
          setIsGenerating(false);
          if (onStoryGenerated) onStoryGenerated(sid, g.title);
        } else if (g.status === "failed") {
          clearInterval(genPollRef.current);
          genPollRef.current = null;
          localStorage.removeItem(ACTIVE_STORY_KEY);
          setError(g.error || "Story generation failed. Please try again.");
          setIsGenerating(false);
        }
      } catch (err) {
        // Story no longer belongs to this session (e.g. a different account logged in) — stop quietly.
        if (err.response?.status === 403 || err.response?.status === 404) {
          clearInterval(genPollRef.current);
          genPollRef.current = null;
          localStorage.removeItem(ACTIVE_STORY_KEY);
        }
      }
    }, 1200);
  }, [onStoryGenerated]);

  // Resume an in-flight (or just-finished) draft left behind in this browser —
  // e.g. the parent started a story, navigated away, and is now back on the home page.
  useEffect(() => {
    if (!user) return;
    const sid = localStorage.getItem(ACTIVE_STORY_KEY);
    if (!sid) return;
    axios.get(`${API}/stories/${sid}/generation-status`, { withCredentials: true })
      .then((res) => {
        const g = res.data;
        if (g.status === "generating") {
          setStoryId(sid);
          setStoryText(g.story_text || "");
          setIsGenerating(true);
          startGenerationPolling(sid);
        } else if (g.status === "ready") {
          setStoryId(sid);
          setStoryText(g.story_text || "");
          setStoryTitle(g.title || "");
          if (g.parent_note) setParentNote(g.parent_note);
          setIsGenerating(false);
          localStorage.removeItem(ACTIVE_STORY_KEY);
        } else {
          localStorage.removeItem(ACTIVE_STORY_KEY);
        }
      })
      .catch(() => localStorage.removeItem(ACTIVE_STORY_KEY));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleGenerate = useCallback(async () => {
    if (!user) {
      onAuthRequired();
      return;
    }
    if (!formData.child_name.trim()) {
      setError("Please enter your child's name");
      return;
    }

    setIsGenerating(true);
    setStoryText("");
    setStoryId(null);
    setStoryTitle("");
    setStoryParts([]);
    setIllustrations({});
    setParentNote(null);
    setError("");
    setShowUpgrade(false);
    setBatchStatus(null);
    localStorage.removeItem(ACTIVE_STORY_KEY);
    if (batchPollRef.current) { clearInterval(batchPollRef.current); batchPollRef.current = null; }
    if (genPollRef.current) { clearInterval(genPollRef.current); genPollRef.current = null; }

    try {
      const { data } = await axios.post(`${API}/stories/generate`, {
        ...formData,
        profile_id: selectedProfileId,
        sibling_name: siblingMode && formData.sibling_name.trim() ? formData.sibling_name.trim() : null,
        sibling_age: siblingMode && formData.sibling_name.trim() ? formData.sibling_age : null,
      }, { withCredentials: true });

      const sid = data.story_id;
      setStoryId(sid);
      localStorage.setItem(ACTIVE_STORY_KEY, sid);

      // Generation now runs server-side in the background (survives this tab closing);
      // poll for progress so it still feels like a live stream while the user stays.
      startGenerationPolling(sid);
    } catch (err) {
      if (err.response?.status === 402) setShowUpgrade(true);
      else if (err.response?.status === 401) onAuthRequired();
      else setError(err.response?.data?.detail || "Story generation failed. Please try again.");
      setIsGenerating(false);
    }
  }, [user, formData, selectedProfileId, siblingMode, onAuthRequired, startGenerationPolling]);

  const handleGenerateIllustration = async (index) => {
    if (!storyId) return;
    setGeneratingIlls((prev) => ({ ...prev, [index]: true }));
    try {
      await axios.post(
        `${API}/stories/${storyId}/illustrations/${index}`, {},
        { withCredentials: true }
      );
      setIllustrations((prev) => ({ ...prev, [index]: true }));
    } catch (err) {
      console.error("Illustration error:", err);
    } finally {
      setGeneratingIlls((prev) => ({ ...prev, [index]: false }));
    }
  };

  const handleGenerateAll = async () => {
    if (!storyId || batchPollRef.current) return;
    try {
      const { data } = await axios.post(
        `${API}/stories/${storyId}/illustrations/generate-all`, {},
        { withCredentials: true }
      );
      setBatchStatus({ ready: data.ready, total: data.total });
      if (data.status === "processing") {
        batchPollRef.current = setInterval(async () => {
          try {
            const res = await axios.get(`${API}/stories/${storyId}/illustrations/status`, { withCredentials: true });
            const readyMap = {};
            res.data.ready.forEach((idx) => { readyMap[idx] = true; });
            setIllustrations((prev) => ({ ...prev, ...readyMap }));
            setBatchStatus({ ready: res.data.ready.length, total: res.data.total });
            if (res.data.ready.length >= res.data.total) {
              clearInterval(batchPollRef.current);
              batchPollRef.current = null;
            }
          } catch (_) {}
        }, 2500);
      }
    } catch (err) {
      console.error("Batch illustration error:", err);
    }
  };

  const illustrationCount = storyParts.filter((p) => p.type === "illustration").length;

  return (
    <section id="generator" data-testid={STORY.generatorSection} className="py-24 lg:py-32 relative">
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(ellipse 70% 50% at 30% 50%, rgba(245,158,11,0.04) 0%, transparent 60%)"
      }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.span
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            className="inline-block text-xs font-mono tracking-[0.2em] uppercase text-amber-300/80 mb-4"
          >
            Story Generator
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white"
          >
            Create your child's<br />
            <span className="gradient-text">personalized story</span>
          </motion.h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* ── Form Panel ── */}
          <motion.div
            initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
            className="lg:col-span-4 glass-card rounded-2xl p-6 lg:sticky lg:top-24"
          >
            <h3 className="font-serif text-lg font-bold text-white mb-6 flex items-center gap-2">
              <BookOpen size={16} className="text-amber-400" />
              Story Details
            </h3>

            <form data-testid={STORY.form} onSubmit={(e) => { e.preventDefault(); handleGenerate(); }} className="space-y-5">
              {/* Child profile quick-fill */}
              {user && profiles.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5 tracking-wider uppercase flex items-center gap-1.5">
                    <Users size={11} /> Story For
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {profiles.map((p) => (
                      <button
                        key={p.profile_id}
                        type="button"
                        data-testid={`generator-profile-chip-${p.profile_id}`}
                        onClick={() => handleSelectProfile(p)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                          selectedProfileId === p.profile_id
                            ? "bg-amber-500/20 border border-amber-400/50 text-amber-300"
                            : "border border-purple-500/20 text-slate-400 hover:text-white hover:border-purple-500/40"
                        }`}
                      >
                        {p.name}, {p.age}
                      </button>
                    ))}
                    <Link
                      to="/my-stories"
                      className="px-3 py-1.5 rounded-full text-xs font-medium border border-dashed border-purple-500/30 text-slate-500 hover:text-amber-300 hover:border-amber-400/40 transition-colors"
                    >
                      + Manage
                    </Link>
                  </div>
                </div>
              )}

              {/* Child's Name */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-medium text-slate-400 tracking-wider uppercase">
                    Child's Name <span className="text-red-400">*</span>
                  </label>
                  <button
                    type="button"
                    data-testid="sibling-mode-toggle"
                    onClick={() => setSiblingMode((v) => !v)}
                    className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-medium transition-colors ${
                      siblingMode
                        ? "bg-amber-500/20 border border-amber-400/50 text-amber-300"
                        : "border border-purple-500/20 text-slate-500 hover:text-white"
                    }`}
                  >
                    <Users2 size={11} /> Sibling Mode {siblingMode ? "On" : ""}
                  </button>
                </div>
                <input
                  data-testid={STORY.childNameInput}
                  type="text"
                  value={formData.child_name}
                  onChange={(e) => setFormData((f) => ({ ...f, child_name: e.target.value }))}
                  placeholder="e.g., Lily"
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-purple-500/20 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-amber-400/50 transition-all"
                />
              </div>

              {/* Age */}
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5 tracking-wider uppercase">
                  Age: <span className="text-amber-400 font-bold">{formData.child_age}</span> years
                </label>
                <input
                  data-testid={STORY.ageSelect}
                  type="range" min={3} max={10} step={1}
                  value={formData.child_age}
                  onChange={(e) => setFormData((f) => ({ ...f, child_age: parseInt(e.target.value) }))}
                  className="w-full audio-progress h-2 cursor-pointer"
                  style={{ background: `linear-gradient(to right, #F59E0B ${(formData.child_age - 3) / 7 * 100}%, rgba(255,255,255,0.1) 0%)` }}
                />
                <div className="flex justify-between text-xs text-slate-600 mt-1">
                  <span>3</span><span>10</span>
                </div>
              </div>

              {/* Sibling fields */}
              <AnimatePresence>
                {siblingMode && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-400/15 space-y-4">
                      <p className="text-[11px] text-amber-300/80 flex items-center gap-1.5">
                        <Users2 size={12} /> Both siblings will star in one shared adventure
                      </p>
                      <div>
                        <label className="block text-xs font-medium text-slate-400 mb-1.5 tracking-wider uppercase">
                          Sibling's Name
                        </label>
                        <input
                          data-testid="sibling-name-input"
                          type="text"
                          value={formData.sibling_name}
                          onChange={(e) => setFormData((f) => ({ ...f, sibling_name: e.target.value }))}
                          placeholder="e.g., Leo"
                          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-purple-500/20 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-amber-400/50 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-400 mb-1.5 tracking-wider uppercase">
                          Sibling's Age: <span className="text-amber-400 font-bold">{formData.sibling_age}</span> years
                        </label>
                        <input
                          data-testid="sibling-age-select"
                          type="range" min={3} max={10} step={1}
                          value={formData.sibling_age}
                          onChange={(e) => setFormData((f) => ({ ...f, sibling_age: parseInt(e.target.value) }))}
                          className="w-full audio-progress h-2 cursor-pointer"
                          style={{ background: `linear-gradient(to right, #F59E0B ${(formData.sibling_age - 3) / 7 * 100}%, rgba(255,255,255,0.1) 0%)` }}
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Theme */}
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5 tracking-wider uppercase">Adventure Theme</label>
                <div className="relative">
                  <select
                    data-testid={STORY.themeSelect}
                    value={formData.theme}
                    onChange={(e) => setFormData((f) => ({ ...f, theme: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-purple-500/20 text-white text-sm focus:outline-none focus:border-amber-400/50 transition-all appearance-none cursor-pointer"
                    style={{ background: "rgba(255,255,255,0.05)" }}
                  >
                    {THEMES.map((t) => (
                      <option key={t.value} value={t.value} style={{ background: "#1a1535" }}>{t.label}</option>
                    ))}
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
                </div>
              </div>

              {/* Illustration Style */}
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5 tracking-wider uppercase">Illustration Style</label>
                <div className="flex flex-wrap gap-2">
                  {ILLUSTRATION_STYLES.map((s) => (
                    <button
                      key={s.value}
                      type="button"
                      data-testid={`style-${s.value}-btn`}
                      onClick={() => setFormData((f) => ({ ...f, illustration_style: s.value }))}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                        formData.illustration_style === s.value
                          ? "bg-amber-500/20 border border-amber-400/50 text-amber-300"
                          : "border border-purple-500/20 text-slate-400 hover:text-white hover:border-purple-500/40"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Companion */}
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5 tracking-wider uppercase">
                  Special Detail <span className="text-slate-600 normal-case text-xs">(optional)</span>
                </label>
                <input
                  data-testid={STORY.companionInput}
                  type="text"
                  value={formData.companion_detail}
                  onChange={(e) => setFormData((f) => ({ ...f, companion_detail: e.target.value }))}
                  placeholder="e.g., stuffed bear named Boo"
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-purple-500/20 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-amber-400/50 transition-all"
                />
              </div>

              {/* Error */}
              {error && (
                <div data-testid="generator-error" className="flex items-start gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20">
                  <AlertCircle size={14} className="text-red-400 mt-0.5 shrink-0" />
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              {/* Upgrade prompt */}
              {showUpgrade && (
                <div data-testid="upgrade-prompt" className="p-4 rounded-xl bg-amber-500/10 border border-amber-400/20">
                  <p className="text-sm text-amber-300 font-medium mb-1">Free story used</p>
                  <p className="text-xs text-slate-400 mb-3">Upgrade to DreamWeaver Unlimited for endless nightly adventures.</p>
                  <button
                    data-testid="upgrade-prompt-cta"
                    type="button"
                    onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })}
                    className="w-full py-2 rounded-lg bg-amber-500 text-black text-xs font-bold hover:bg-amber-400 transition-colors"
                  >
                    Upgrade for $7/month
                  </button>
                </div>
              )}

              {/* Generate Button */}
              <button
                data-testid={STORY.generateButton}
                type="submit"
                disabled={isGenerating || !formData.child_name.trim()}
                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-purple-600 to-amber-500 text-white font-semibold text-sm hover:shadow-lg hover:shadow-purple-500/30 transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isGenerating ? (
                  <><Loader2 size={15} className="animate-spin" />Weaving magic...</>
                ) : (
                  <><Sparkles size={15} />Generate Story</>
                )}
              </button>

              {!user && (
                <p className="text-center text-xs text-slate-500">
                  Free account required •{" "}
                  <button
                    type="button"
                    onClick={onAuthRequired}
                    className="text-amber-400 hover:text-amber-300"
                  >Sign up free</button>
                </p>
              )}
            </form>
          </motion.div>

          {/* ── Story Display Panel ── */}
          <div className="lg:col-span-8">
            <AnimatePresence mode="wait">
              {!storyText && !isGenerating ? (
                <motion.div
                  key="placeholder"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="glass-card rounded-2xl p-12 flex flex-col items-center justify-center text-center min-h-[400px]"
                >
                  <div className="w-20 h-20 rounded-full bg-purple-500/10 border border-purple-500/20 flex items-center justify-center mb-6">
                    <BookOpen size={32} className="text-purple-400" />
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-white mb-2">Your story will appear here</h3>
                  <p className="text-sm text-slate-500 max-w-sm">
                    Fill in your child's name and press Generate. A personalized 20-minute bedtime adventure with 10+ illustrations will stream right here.
                  </p>
                </motion.div>
              ) : (
                <motion.div
                  key="story"
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                  data-testid={STORY.storyDisplay}
                  className="glass-card rounded-2xl overflow-hidden"
                >
                  {/* Story Header */}
                  {storyTitle && (
                    <div className="px-8 pt-8 pb-4 border-b border-purple-500/10">
                      <span className="text-xs font-mono text-amber-300/60 tracking-widest uppercase mb-2 block">DreamWeaver Story</span>
                      <h2 data-testid={STORY.storyTitle} className="font-serif text-2xl font-bold gradient-text">
                        {storyTitle}
                      </h2>
                      {wordCount > 0 && (
                        <p className="text-xs text-slate-500 mt-1">~{wordCount.toLocaleString()} words · ~{Math.ceil(wordCount / 130)} min read</p>
                      )}
                    </div>
                  )}

                  {isGenerating && (
                    <div data-testid="generation-survives-navigation-note" className="px-8 pt-6 pb-2 flex items-center gap-2 text-xs text-slate-500">
                      <Sparkles size={12} className="text-amber-400/70" />
                      Feel free to leave this page — your story keeps weaving in the background and will be waiting in My Stories.
                    </div>
                  )}

                  {/* Story Body */}
                  <div
                    ref={storyScrollRef}
                    data-testid={STORY.storyText}
                    className="p-6 sm:p-8 max-h-[60vh] overflow-y-auto"
                  >
                    {isGenerating && storyParts.length === 0 ? (
                      <div className={`story-body ${isGenerating ? "cursor-blink" : ""}`}>
                        {storyText.split(/\n\n+/).filter(Boolean).map((p, i) => (
                          <p key={i}>{p}</p>
                        ))}
                        {!storyText && (
                          <div className="flex items-center gap-3 text-slate-500">
                            <Loader2 size={14} className="animate-spin" />
                            <span className="text-sm italic font-serif">Your story is being crafted...</span>
                          </div>
                        )}                      </div>
                    ) : (
                      storyParts.map((part, i) =>
                        part.type === "text" ? (
                          <StoryTextBlock key={i} content={part.content} />
                        ) : (
                          <IllustrationCard
                            key={i}
                            storyId={storyId}
                            prompt={part.prompt}
                            index={part.index}
                            illustrations={illustrations}
                            onGenerate={handleGenerateIllustration}
                            generating={generatingIlls}
                          />
                        )
                      )
                    )}
                  </div>

                  {/* Post-generation actions */}
                  {!isGenerating && storyId && illustrationCount > 0 && (
                    <div className="px-8 py-5 border-t border-purple-500/10 flex flex-wrap items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        {batchStatus && batchStatus.ready < batchStatus.total ? (
                          <div
                            data-testid={STORY.batchProgress}
                            className="flex items-center gap-2 px-4 py-2 rounded-full bg-purple-600/10 border border-purple-500/20 text-purple-300 text-xs font-medium"
                          >
                            <Loader2 size={13} className="animate-spin" />
                            Painting illustrations... {batchStatus.ready}/{batchStatus.total}
                          </div>
                        ) : (
                          <button
                            data-testid={STORY.generateAllBtn}
                            onClick={handleGenerateAll}
                            className="flex items-center gap-2 px-4 py-2 rounded-full bg-purple-600/20 border border-purple-500/30 text-purple-300 text-xs font-medium hover:bg-purple-600/40 transition-colors"
                          >
                            <Image size={13} />
                            Generate All {illustrationCount} Illustrations
                          </button>
                        )}
                        <Link
                          to={`/story/${storyId}`}
                          data-testid="open-in-library-link"
                          className="flex items-center gap-2 px-4 py-2 rounded-full border border-purple-500/30 text-slate-300 text-xs font-medium hover:border-amber-400/40 hover:text-amber-300 transition-colors"
                        >
                          <BookOpen size={13} />
                          Open in Library
                        </Link>
                      </div>
                      <button
                        onClick={() => document.getElementById("voices")?.scrollIntoView({ behavior: "smooth" })}
                        className="flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/20 border border-amber-400/30 text-amber-300 text-xs font-medium hover:bg-amber-500/30 transition-colors"
                      >
                        <span>Narrate this story</span>
                      </button>
                    </div>
                  )}

                  {/* Parent Note */}
                  {parentNote && !isGenerating && (
                    <div className="mx-8 mb-6 p-4 rounded-xl bg-amber-500/10 border border-amber-400/20">
                      <p className="text-xs text-slate-400 uppercase tracking-widest font-mono mb-1">For Parents</p>
                      <p className="text-sm text-amber-200">{parentNote}</p>
                      <button
                        onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })}
                        className="mt-3 px-4 py-1.5 rounded-full bg-amber-500 text-black text-xs font-bold hover:bg-amber-400 transition-colors"
                      >
                        Unlock Unlimited Stories — $7/month
                      </button>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
